import { useEffect, useState } from "react";
import { Loader2, Minus, Plus } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Slider } from "../ui/slider";
import { api, errMsg, money } from "../../lib/api";
import { useAuth } from "../../lib/auth";

export const TicketDialog = ({ concert, onClose }) => {
  const { user } = useAuth();
  const [qty, setQty] = useState(0);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setQty(0);
    setBusy(false);
  }, [concert]);

  if (!concert) return null;
  const left = Math.max(0, concert.capacity - concert.tickets_sold);
  const max = Math.min(100, left);
  const mail = email || (user && user.email) || "";
  const set = (v) => setQty(Math.max(0, Math.min(max, Number.isNaN(v) ? 0 : v)));

  const buy = async () => {
    if (!/^\S+@\S+\.\S+$/.test(mail)) return toast.error("Enter your email for the tickets");
    setBusy(true);
    try {
      const { checkout_url } = await api.checkout([{ type: "ticket", ref_id: concert.id, quantity: qty }], mail);
      window.location.href = checkout_url;
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!concert} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0d0f14] border border-[#222634] text-slate-100 max-w-md" data-testid="ticket-dialog">
        <DialogHeader>
          <DialogTitle className="font-cond uppercase tracking-wide text-3xl text-white leading-none">{concert.title}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate-400">
          {concert.venue} · {concert.city}<br />
          <span className="font-mono-ui text-lime">{concert.date} · {concert.time}</span>
        </p>
        <div className="mt-4">
          <div className="flex items-center justify-between mb-3">
            <span className="font-cond uppercase tracking-widest text-sm text-slate-400">Tickets (0–100)</span>
            <span className="text-xs text-slate-500" data-testid="tickets-left">{left} left</span>
          </div>
          <div className="flex items-center gap-3">
            <button className="btn-line rounded-full w-10 h-10 flex items-center justify-center" onClick={() => set(qty - 1)} disabled={qty <= 0} data-testid="ticket-qty-minus"><Minus className="w-4 h-4" /></button>
            <input type="number" min={0} max={max} value={qty} onChange={(e) => set(parseInt(e.target.value, 10))}
              className="field text-center font-mono-ui text-xl !w-24" data-testid="ticket-qty-input" />
            <button className="btn-line rounded-full w-10 h-10 flex items-center justify-center" onClick={() => set(qty + 1)} disabled={qty >= max} data-testid="ticket-qty-plus"><Plus className="w-4 h-4" /></button>
          </div>
          <Slider className="mt-5 lime-slider" min={0} max={Math.max(max, 1)} step={1} value={[qty]} onValueChange={([v]) => set(v)} disabled={max === 0} data-testid="ticket-qty-slider" />
        </div>
        <div className="flex justify-between items-baseline mt-4 font-cond uppercase tracking-widest">
          <span className="text-slate-400">{qty} × {money(concert.price)}</span>
          <span className="text-3xl text-lime" data-testid="ticket-total">{money(qty * concert.price)}</span>
        </div>
        <input className="field mt-2" type="email" placeholder="Email for your tickets" value={mail} onChange={(e) => setEmail(e.target.value)} data-testid="ticket-email-input" />
        <button onClick={buy} disabled={qty === 0 || busy} data-testid="ticket-buy-button"
          className="btn-lime w-full rounded-full py-3.5 mt-2 font-cond uppercase tracking-widest font-bold text-lg flex items-center justify-center gap-2">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />}
          {max === 0 ? "Sold out" : qty === 0 ? "Pick how many tickets" : `Buy ${qty} ticket${qty > 1 ? "s" : ""}`}
        </button>
      </DialogContent>
    </Dialog>
  );
};
